/**
 * payrollProfile — the paying facts about a subcontractor: who to pay, into
 * which account, under which name, and whether they issue a sales invoice.
 *
 * WHY THIS IS NOT `iSyn2_Subcontractors`. That list already exists and is
 * live: `SubconRegistryService` reads it for the assignment board, sewer
 * intelligence and the grading screens, and `subcontractors.ts` seeds it with
 * the shipped 57. It answers "who can sew this and where are they" — cluster,
 * manpower, tenure. It has no bank account and no TIN because nothing that
 * reads it has any business holding one.
 *
 * Widening that list would put payment details behind every screen that
 * already reads it, and would put a payroll deploy in the path of the
 * assignment board. So payroll gets its OWN list, joined on the same ledger
 * key, and the two never write each other. Concretely: registering a new
 * subcontractor stays a BQC1 action, and giving that subcontractor a bank
 * account stays a payroll action.
 *
 * SOURCE OF THE FIELDS: the workbook's two masterlist sheets, CONTRACTORS
 * DATABASE (BIR: name, ID, company, BPI account, address, section, date
 * started, birthdate, cellphone, email, BIR CoR, schedule) and CONTRACTORS
 * DATABASE (NON BIR) (the same minus company). One list here, with `bir`
 * telling the two apart, because they are two sheets for one reason —
 * BIR-registered subcontractors get the 12% Registration & Sales Invoice
 * uplift and non-BIR ones do not — and that is a field, not a table.
 *
 * Pure and I/O-free; SubconPayrollService does the reading and writing.
 */
import type { BirSide } from "./payrollMath";
import { canonKey } from "../mfg-workcenters/subcontractors";

export interface IPayrollProfile {
  /** SharePoint item id, absent for a profile not saved yet. */
  spId?: number;
  /**
   * The join key — the same upper-cased surname the WIP grid writes into
   * AssignedSewer and `subcontractors.ts` keys on. Matching on the full
   * masterlist name instead would silently unpick every BQC5 attribution.
   */
  ledgerKey: string;
  /** Masterlist name, "SURNAME, GIVEN NAMES" — printed on the payslip. */
  fullName: string;
  /** "IG00108". The payout reference takes its digits (see contractorSeq). */
  contractorId: string;
  bir: BirSide;
  /** Registered business name. BIR side only; blank for non-BIR. */
  companyName?: string;
  /** BIR Certificate of Registration / TIN, printed on the BIR payslip. */
  tin?: string;
  /**
   * Account NAME on the receiving bank account — "LERMA VEQUISO".
   *
   * Held separately from `fullName` because they genuinely differ: the
   * masterlist writes "VEQUISO, LERMA PERALTA" and the bank holds "LERMA
   * VEQUISO", and the bank file is rejected on a name mismatch. This is the
   * RECORDS sheet's own "BPI NAME" column, which exists for the same reason.
   */
  bankName?: string;
  /** Account number, or "GCASH" where the masterlist says so. Kept as text —
   *  a leading zero is part of an account number, and a number loses it. */
  bankAccount?: string;
  modeOfPayment: PaymentMode;
  /** Town/city, printed as LOCATION. */
  location?: string;
  /** The masterlist's SECTION column — the same value as location on the
   *  non-BIR sheet, a delivery area on the BIR one. Printed as SECTION. */
  section?: string;
  /** Payout day, e.g. "SUNDAY" — the masterlist's SCHEDULE column. */
  schedule?: string;
  /** ISO date. */
  dateStarted?: string | null;
  cellphone?: string;
  email?: string;
  /** Position line on the payslip. The workbook writes "SEWER CONTRACTOR"
   *  on the BIR sheet and "SEWER SUBCONTRACTOR" on the other; both sides are
   *  subcontractors, so both say so here and the BIR distinction is carried
   *  by the TIN and 12% lines, which is where it actually applies. */
  position?: string;
  /** False for a subcontractor who no longer takes work. Kept, never
   *  deleted: their payout history joins on this key. */
  active: boolean;
}

/** The modes the workbook's MODE OF PAYMENT column actually carries. */
export const PAYMENT_MODES = ["BANK TRANSFER", "GCASH", "CASH"] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];

export function isPaymentMode(v: string | null | undefined): v is PaymentMode {
  return (PAYMENT_MODES as readonly string[]).includes(String(v ?? "").toUpperCase().trim());
}

/** Normalise whatever the list holds into a mode, defaulting to the one 90%
 *  of the records use. */
export function paymentMode(v: string | null | undefined): PaymentMode {
  const s = String(v ?? "").toUpperCase().trim();
  return isPaymentMode(s) ? (s as PaymentMode) : "BANK TRANSFER";
}

export const DEFAULT_POSITION: Record<BirSide, string> = {
  BIR: "SEWER SUBCONTRACTOR",
  "NON-BIR": "SEWER SUBCONTRACTOR",
};

/** Profiles by ledger key, last one wins — the shape every join here wants. */
export function profilesByKey(rows: readonly IPayrollProfile[]): Map<string, IPayrollProfile> {
  const m = new Map<string, IPayrollProfile>();
  for (const r of rows) {
    const k = canonKey(r.ledgerKey);
    if (k) m.set(k, r);
  }
  return m;
}

/** What is missing before a profile can be paid. Empty means payable. */
export type ProfileGap = "contractorId" | "bankName" | "bankAccount";

/**
 * Blocking gaps for one profile, in the order a clerk would fix them.
 *
 * A missing contractor ID blocks everything — the payout reference is built
 * from it. Bank name and account block only a BANK TRANSFER: a cash or GCash
 * payout has no bank row to write, so demanding an account number for one
 * would flag half the non-BIR roster as broken when nothing is wrong.
 */
export function profileGaps(p: IPayrollProfile): ProfileGap[] {
  const gaps: ProfileGap[] = [];
  if (!/\d/.test(p.contractorId ?? "")) gaps.push("contractorId");
  if (p.modeOfPayment === "BANK TRANSFER") {
    if (!p.bankName?.trim()) gaps.push("bankName");
    if (!p.bankAccount?.trim()) gaps.push("bankAccount");
  }
  return gaps;
}

export const GAP_LABEL: Record<ProfileGap, string> = {
  contractorId: "No contractor ID — the payout reference cannot be built",
  bankName: "No BPI account name — the bank file would be rejected",
  bankAccount: "No account number — the bank file would be rejected",
};
